import { lazy, Suspense, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useDocumentMetadata } from '@hooks/useDocumentMetadata'
import HomeScreen from '@home/HomeScreen'
import ScreenWrapper from '@components/layout/ScreenWrapper'
import ScreenFallback from '@components/layout/ScreenFallback'

/**
 * Lazy-loaded screen components
 * Loaded on-demand based on route to reduce initial bundle
 */
const MemoramaScreen = lazy(() => import('@games/memorama')
  .then(m => ({ default: m.MemoramaScreen })))
const PuzzleScreen = lazy(() => import('@games/puzzle')
  .then(m => ({ default: m.PuzzleScreen })))

/**
 * App - Main application component
 *
 * Routes to appropriate screen orchestrator based on URL path:
 * - /:lang → HomeScreen (landing/game selection)
 * - /:lang/memorama → MemoramaScreen (orchestrator)
 * - /:lang/puzzle → PuzzleScreen (orchestrator)
 *
 * Note: Game-specific providers (GameProvider, PuzzleProvider) are scoped
 * at the route level in AppRouter.jsx, not here.
 */

function App () {
  useDocumentMetadata()
  const location = useLocation()

  // Enable transitions after React hydration
  useEffect(() => {
    document.documentElement.classList.add('is-ready')
  }, [])

  // Determine screen component based on route
  const segments = location.pathname.split('/').filter(Boolean) // Remove empty segments
  const routeType = segments[1] ?? ''

  let ScreenComponent = HomeScreen // Default for /:lang

  if (routeType === 'memorama') {
    ScreenComponent = MemoramaScreen
  } else if (routeType === 'puzzle') {
    ScreenComponent = PuzzleScreen
  }

  return (
    <div className='app-layout'>
      <ScreenWrapper>
        <Suspense fallback={<ScreenFallback />}>
          <ScreenComponent />
        </Suspense>
      </ScreenWrapper>
    </div>
  )
}

export default App
